import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const { t } = useLang();
  const { checkAuth } = useAuth();
  const { clear } = useCart();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("pending");
  const [kind, setKind] = useState("subscription");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.kind) setKind(res.data.kind);
        if (res.data.payment_status === "paid") { setStatus("paid"); clear(); checkAuth(); return; }
        if (res.data.status === "expired" || res.data.payment_status === "failed") { setStatus("error"); return; }
      } catch {}
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, checkAuth, clear]);

  const successCopy = kind === "donation"
    ? { title: t("pay_donation_thanks"), desc: t("pay_donation_desc"), to: "/support", label: t("nav_support") }
    : kind === "order"
    ? { title: t("pay_order_thanks"), desc: t("pay_order_desc"), to: "/profile", label: t("nav_profile") }
    : { title: t("pay_success"), desc: t("pay_success_desc"), to: "/profile", label: t("nav_profile") };

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center">
      {status === "pending" ? (
        <><Loader2 className="w-14 h-14 text-amber-500 mx-auto animate-spin mb-6" /><p className="text-stone-300 text-lg">{t("pay_pending")}</p></>
      ) : status === "paid" ? (
        <div data-testid="payment-success">
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-amber-100 mb-3">{successCopy.title}</h1>
          <p className="text-stone-400 mb-8">{successCopy.desc}</p>
          <Link to={successCopy.to} className="inline-block bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-6 py-3 rounded-md">{successCopy.label}</Link>
        </div>
      ) : (
        <><XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" /><h1 className="font-serif text-2xl text-stone-100 mb-6">{t("pay_cancel")}</h1><Link to="/membership" className="text-amber-400">{t("nav_membership")}</Link></>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-cancel">
      <XCircle className="w-16 h-16 text-stone-500 mx-auto mb-6" />
      <h1 className="font-serif text-3xl text-stone-100 mb-3">{t("pay_cancel")}</h1>
      <Link to="/" className="inline-block mt-4 text-amber-400">{t("pay_back")}</Link>
    </div>
  );
}
